import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react";
import {
    computeSuperformulaPath,
    type SuperformulaParams,
    type SuperformulaPreset,
} from "../../utils/superformula";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";

interface SuperformulaState {
    // Superformula parameters
    m: number;
    a: number;
    b: number;
    n1: number;
    n2: number;
    n3: number;
    // Canvas
    size: number;
    samples: number;
    // Appearance
    fillColor: string;
    strokeColor: string;
    bgColor: string;
    strokeWidth: number;
    fillShapes: boolean;
    /** When true, the downloaded SVG includes the background rect (matches preview). */
    exportBackground: boolean;
    // Setters
    setM: (v: number) => void;
    setA: (v: number) => void;
    setB: (v: number) => void;
    setN1: (v: number) => void;
    setN2: (v: number) => void;
    setN3: (v: number) => void;
    setSize: (v: number) => void;
    setSamples: (v: number) => void;
    setFillColor: (v: string) => void;
    setStrokeColor: (v: string) => void;
    setBgColor: (v: string) => void;
    setStrokeWidth: (v: number) => void;
    setFillShapes: (v: boolean) => void;
    setExportBackground: (v: boolean) => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
    randomizeParameters: () => void;
    // Derived
    viewBoxAttr: string;
    pathD: string;
    canExport: boolean;
    buildExportSvgString: () => string;
    handleDownload: () => void;
}

const SuperformulaContext = createContext<SuperformulaState | null>(null);

const DEFAULT_SUPERFORMULA_PRESET: Record<string, unknown> = {
    m: 5,
    a: 1,
    b: 1,
    n1: 1,
    n2: 1,
    n3: 1,
    size: 800,
    samples: 720,
    fillColor: "#4a6fa5",
    strokeColor: "#2d3e5f",
    bgColor: "#f5f0e8",
    strokeWidth: 2,
    fillShapes: true,
    exportBackground: true,
};

export function useSuperformula() {
    const ctx = useContext(SuperformulaContext);
    if (!ctx)
        throw new Error(
            "useSuperformula must be used within SuperformulaProvider",
        );
    return ctx;
}

function seededRNG(seed: string): () => number {
    let s = [...seed].reduce((a, c) => a + c.charCodeAt(0), 0) || 1;
    return () => {
        s = (s * 9301 + 49297) % 233280;
        return s / 233280;
    };
}

export function SuperformulaProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const [m, setM] = useState(5);
    const [a, setA] = useState(1);
    const [b, setB] = useState(1);
    const [n1, setN1] = useState(1);
    const [n2, setN2] = useState(1);
    const [n3, setN3] = useState(1);
    const [size, setSize] = useState(800);
    const [samples, setSamples] = useState(720);
    const [fillColor, setFillColor] = useState("#4a6fa5");
    const [strokeColor, setStrokeColor] = useState("#2d3e5f");
    const [bgColor, setBgColor] = useState("#f5f0e8");
    const [strokeWidth, setStrokeWidth] = useState(2);
    const [fillShapes, setFillShapes] = useState(true);
    const [exportBackground, setExportBackground] = useState(true);

    const viewBoxAttr = useMemo(
        () => `${-size / 2} ${-size / 2} ${size} ${size}`,
        [size],
    );

    const params = useMemo<SuperformulaParams>(
        () => ({ m, a, b, n1, n2, n3, size, samples }),
        [m, a, b, n1, n2, n3, size, samples],
    );

    const pathD = useMemo(() => computeSuperformulaPath(params), [params]);

    const canExport = pathD.length > 0;

    const buildExportSvgString = useCallback((): string => {
        if (!pathD) return "";
        const strokeAttr = `stroke="${strokeColor}" stroke-width="${strokeWidth}"`;
        const fillAttr = fillShapes ? `fill="${fillColor}"` : `fill="none"`;
        const bgLine = exportBackground
            ? `  <rect x="${-size / 2}" y="${-size / 2}" width="${size}" height="${size}" fill="${bgColor}" />\n`
            : "";
        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}">
${bgLine}  <path d="${pathD}" ${fillAttr} ${strokeAttr} />
</svg>`;
    }, [
        pathD,
        fillColor,
        strokeColor,
        bgColor,
        strokeWidth,
        fillShapes,
        exportBackground,
        viewBoxAttr,
        size,
    ]);

    const handleDownload = useCallback(() => {
        const svgString = buildExportSvgString();
        if (!svgString) return;
        const blob = new Blob([svgString], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `superformula-m${m}-${Date.now()}.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [
        buildExportSvgString,
        m,
    ]);

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            m,
            a,
            b,
            n1,
            n2,
            n3,
            size,
            samples,
            fillColor,
            strokeColor,
            bgColor,
            strokeWidth,
            fillShapes,
            exportBackground,
        }),
        [
            m,
            a,
            b,
            n1,
            n2,
            n3,
            size,
            samples,
            fillColor,
            strokeColor,
            bgColor,
            strokeWidth,
            fillShapes,
            exportBackground,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.m != null) setM(values.m as number);
        if (values.a != null) setA(values.a as number);
        if (values.b != null) setB(values.b as number);
        if (values.n1 != null) setN1(values.n1 as number);
        if (values.n2 != null) setN2(values.n2 as number);
        if (values.n3 != null) setN3(values.n3 as number);
        if (values.size != null) setSize(values.size as number);
        if (values.samples != null) setSamples(values.samples as number);
        if (values.fillColor != null) setFillColor(values.fillColor as string);
        if (values.strokeColor != null)
            setStrokeColor(values.strokeColor as string);
        if (values.bgColor != null) setBgColor(values.bgColor as string);
        if (values.strokeWidth != null)
            setStrokeWidth(values.strokeWidth as number);
        if (values.fillShapes != null)
            setFillShapes(values.fillShapes as boolean);
        if (values.exportBackground != null)
            setExportBackground(values.exportBackground as boolean);
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: "superformula",
        defaultPreset: DEFAULT_SUPERFORMULA_PRESET,
        getPresetValues,
        applyPreset,
    });

    const randomizeParameters = useCallback(() => {
        const rng = seededRNG(Date.now().toString());
        const randF = (lo: number, hi: number, dp = 2) =>
            parseFloat((rng() * (hi - lo) + lo).toFixed(dp));

        // m: favor small integers for recognizable symmetry, occasionally fractional
        const mVals = [
            2, 3, 4, 5, 6, 7, 8, 3, 4, 5, 5, 6, 2.5, 3.5, 4.5, 7, 8, 10, 12, 14,
            16, 18, 20, 22, 24, 26, 28, 30,
        ];
        setM(mVals[Math.floor(rng() * mVals.length)] ?? 5);
        setA(randF(0.5, 2.5));
        setB(randF(0.5, 2.5));
        setN1(randF(0.1, 10));
        setN2(randF(0.1, 14));
        setN3(randF(0.1, 14));
    }, []);

    const value: SuperformulaState = {
        m,
        a,
        b,
        n1,
        n2,
        n3,
        size,
        samples,
        fillColor,
        strokeColor,
        bgColor,
        strokeWidth,
        fillShapes,
        exportBackground,
        setM,
        setA,
        setB,
        setN1,
        setN2,
        setN3,
        setSize,
        setSamples,
        setFillColor,
        setStrokeColor,
        setBgColor,
        setStrokeWidth,
        setFillShapes,
        setExportBackground,
        applyPreset,
        getPresetValues,
        resetParameters,
        randomizeParameters,
        viewBoxAttr,
        pathD,
        canExport,
        buildExportSvgString,
        handleDownload,
    };

    return (
        <SuperformulaContext.Provider value={value}>
            {children}
        </SuperformulaContext.Provider>
    );
}
